import { ActorState, normalizeActorState } from "../../../models/ActorState";
import Interaction from "../../../models/interactions/Interaction";
import { InteractionProperty } from "../../../models/InteractionProperty";
import { ISetCamera } from "../../../models/interactions/SetCamera";
import Take3D, {
  CreateTake3D,
  getTake3DSequence,
  normalizeTake3D,
  repairTake3DActorStates,
} from "../../../models/Take3D";
import {
  TrainingState,
  selectInteractionIndexById,
  selectSelectedElementType,
  selectTakeIndexById,
} from "../TrainingSlice";
import {
  findQuizFeedbackInteraction,
  getNextSelectedAfterFeedbackRemoval,
  insertQuizFeedbackInteraction,
  removeQuizFeedbackInteraction,
  reorderQuizFeedbackInteractions,
  replaceQuizFeedbackInteraction,
} from "../helpers/QuizFeedbackLocator";
import { UpdatePropertyOfInteraction } from "../helpers/TrainingSliceHelper";
import { TrainingSliceStrategy } from "./TrainingSliceStrategy";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import { PropTakeState } from "../../../models/PropTakeState";
import {
  getEnvironmentPropPositionId,
  PropType,
} from "../../../models/Prop";
import { TrainingSliceStrategy3DHelper } from "./TrainingSliceStrategy3DHelper";
import { isHeldByAttachment } from "../../../helpers/TakeEndState";
import { ITransition } from "./../../../models/interactions/Transition";
import { normalizeTransition } from "../../../models/interactions/TransitionDefaults";
import { TakeAttachment } from "../../../models/TakeAttachment";
import { ISequence } from "../../../models/interactions/SequenceModel";
import {
  areMoveSourcesFeedbackEligible,
  moveInteraction,
  moveInteractionsBatch,
} from "../helpers/InteractionMove";
import { FEEDBACK_ELIGIBLE_TYPES_3D } from "../../../models/QuizFeedbackHelper";
import { moveSequenceSelection } from "../helpers/SequenceMove";

export const TrainingSliceStrategy3D: TrainingSliceStrategy = {
  setSelectedTake(state, takeId) {
    if (!state.takes3D.find((take) => take.id === takeId)) return {};

    return {
      selectedTake: takeId,
      selectedElementId: takeId,
      selectedElementIsInteraction: false,
      selectedElementIsTake: true,
      selectedSequenceTakeId: "",
    };
  },
  addNewTake(state, name, assetId, transition?: ITransition, clean?: boolean) {
    if (!transition) return {};

    const copy = [...state.takes3D];
    let actors: ActorState[] = [];
    let props: PropTakeState[] = [];
    let situations: Record<string, string> = {};
    let attachments: Record<string, TakeAttachment> = {};

    // A clean take is the take we already build when the storyboard is empty:
    // no carry-over, so it takes the same path the very first take does.
    // Environment prop states below are deliberately outside this — they belong
    // to the environment, not to the previous take's authoring.
    const inherit = !clean && state.takes3D.length > 0;

    if (inherit) {
      actors = [...state.takes3D[state.takes3D.length - 1].actors];
      actors = actors.map((actor) => ({ ...actor, id: GetNewGuid() }));

      props = [...state.takes3D[state.takes3D.length - 1].props];
      props = props.map((prop) => ({ ...prop }));

      situations = { ...state.takes3D[state.takes3D.length - 1].situations };
      attachments = { ...state.takes3D[state.takes3D.length - 1].attachments };
    }
    props = ensureEnvironmentPropStates(state, props);

    let newTake = CreateTake3D(
      name ?? "Unassigned Take",
      actors,
      props,
      transition,
      situations,
      attachments
    );

    // Rebuilds attachments from the previous take's grab/attach interactions.
    // With nothing inherited there is nothing to attach, so a clean take skips
    // it rather than re-coupling itself to the take before it.
    if (inherit) {
      newTake = TrainingSliceStrategy3DHelper.updateActorProp(
        copy[copy.length - 1],
        newTake
      );
    }

    copy.push(newTake);

    return {
      takes3D: copy,
      selectedTake: newTake.id,
      selectedElementId: newTake.id,
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      selectedSequenceTakeId: "",
    };
  },
  addTakeCopy(state, take, transition?: ITransition, interactions?: Interaction[], sequence?: ISequence) {
    if (!transition) return {};

    const originalTake = normalizeTake3D(take as Take3D);
    let copiedActors: ActorState[] = originalTake.actors.map((actor) => ({
      ...actor,
      id: GetNewGuid(),
    }));
    const copiedProps: PropTakeState[] = ensureEnvironmentPropStates(
      state,
      originalTake.props.map((prop) => ({
        ...prop,
      }))
    );
    const copiedSituations = { ...originalTake.situations };
    const copiedAttachments = { ...originalTake.attachments };

    const copy = [...state.takes3D];

    const newTake = CreateTake3D(
      originalTake.name ?? "Unassigned Take",
      copiedActors,
      copiedProps,
      transition,
      copiedSituations,
      copiedAttachments,
      originalTake.interactionPointId ?? undefined,
      originalTake.mainCamera
    );

    newTake.interactionStartDelay = originalTake.interactionStartDelay;
    if (originalTake.startViewMode !== undefined) {
      newTake.startViewMode = originalTake.startViewMode;
    }
    if (originalTake.startPropInspect !== undefined) {
      newTake.startPropInspect = { ...originalTake.startPropInspect };
    }

    newTake.interactions = interactions
      ? interactions.map((interaction) => ({
          ...interaction,
        }))
      : [];

    newTake.sequence = sequence
      ? {
          ...sequence,
          nodes: [...sequence.nodes],
          edges: [...sequence.edges],
        }
      : newTake.sequence;

    const indexSelectedTake = state.selectedElementIsTake
      ? copy.findIndex((t) => t.id === state.selectedElementId)
      : -1;

    if (indexSelectedTake !== -1) {
      copy.splice(indexSelectedTake + 1, 0, newTake);
    } else {
      copy.push(newTake);
    }

    return {
      takes3D: copy,
      selectedTake: newTake.id,
      selectedElementId: newTake.id,
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  setSelectedInteraction(state, interactionId) {
    const transitionParent = state.takes3D.find(
      (take) => take.transition?.id === interactionId
    );
    if (transitionParent) {
      return {
        selectedTake: transitionParent.id,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
      };
    }

    const camParent = state.takes3D.find(
      (take) => take.mainCamera.id === interactionId
    );
    if (camParent) {
      return {
        selectedTake: camParent.id,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
      };
    }

    const parent = state.takes3D.find(
      (take) =>
        take.interactions.findIndex(
          (interaction) => interaction.id === interactionId
        ) !== -1
    );
    if (parent) {
      return {
        selectedTake: parent.id,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
      };
    }

    const actorParent = state.takes3D.find(
      (take) =>
        take.actors.findIndex((actor) => actor.id === interactionId) !== -1
    );
    if (actorParent) {
      return {
        selectedTake: actorParent.id,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
      };
    }

    const feedbackLocation = findQuizFeedbackInteraction(
      state.takes3D,
      interactionId
    );
    if (feedbackLocation) {
      return {
        selectedTake: feedbackLocation.takeId,
        selectedElementId: interactionId,
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
      };
    }

    return {};
  },
  addInteractionToTake(state, interaction: Interaction, takeId) {
    const copiedTakes = [...state.takes3D];
    const index = copiedTakes.findIndex((take) => take.id === takeId);
    const takeCopy = copiedTakes[index];
    if (!takeCopy) return {};

    const copiedInteractions = [...takeCopy.interactions, interaction];
    copiedTakes[index] = {
      ...copiedTakes[index],
      interactions: copiedInteractions,
    };

    return {
      selectedTake: takeId,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      isDirty: true,
      takes3D: copiedTakes,
    };
  },
  addInteractionToSelectedTake(state, interaction) {
    const takeIndex = selectTakeIndexById(state.takes3D, state.selectedTake);
    if (takeIndex === -1) return {};

    const takeCopy = { ...state.takes3D[takeIndex] };
    takeCopy.interactions.push(interaction);
    const takesCopy = [...state.takes3D];
    takesCopy[takeIndex] = takeCopy;

    return {
      takes3D: takesCopy,
      selectedTake: state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  addInteractionCopy(state, interaction) {
    const takesCopy = [...state.takes3D];
    const index = selectTakeIndexById(takesCopy, state.selectedTake);
    if (index === -1) return {};

    if (state.selectedElementIsInteraction) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes3D,
        state.selectedElementId
      );
      if (feedbackLocation) {
        if (!FEEDBACK_ELIGIBLE_TYPES_3D.has(interaction.type)) return {};
        const newTakes = insertQuizFeedbackInteraction(
          state.takes3D,
          feedbackLocation.quizId,
          feedbackLocation.slot,
          interaction,
          feedbackLocation.feedbackIndex + 1
        );
        if (!newTakes) return {};
        return {
          takes3D: newTakes,
          selectedTake: feedbackLocation.takeId,
          selectedElementId: interaction.id,
          selectedElementIsTake: false,
          selectedElementIsInteraction: true,
          selectedSequenceTakeId: "",
          isDirty: true,
        };
      }

      const interactionIndex = selectInteractionIndexById(
        takesCopy[index],
        state.selectedElementId
      );
      takesCopy[index].interactions.splice(interactionIndex + 1, 0, interaction);
    } else {
      takesCopy[index].interactions.unshift(interaction);
    }

    return {
      takes3D: takesCopy,
      selectedTake: state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  addInteractionToQuizFeedback(state, quizId, slot, interaction) {
    if (!FEEDBACK_ELIGIBLE_TYPES_3D.has(interaction.type)) return {};
    const newTakes = insertQuizFeedbackInteraction(
      state.takes3D,
      quizId,
      slot,
      interaction
    );
    if (!newTakes) return {};

    const parentTake = newTakes.find((take) =>
      take.interactions.some((i) => i.id === quizId)
    );

    return {
      takes3D: newTakes,
      selectedTake: parentTake?.id ?? state.selectedTake,
      selectedElementId: interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  reorderQuizFeedbackInteractions(state, quizId, slot, startIndex, endIndex) {
    const newTakes = reorderQuizFeedbackInteractions(
      state.takes3D,
      quizId,
      slot,
      startIndex,
      endIndex
    );
    if (!newTakes) return {};

    return {
      takes3D: newTakes,
      isDirty: true,
    };
  },
  deleteInteraction(state, interaction) {
    const takesCopy = [...state.takes3D];
    const parentIndex = takesCopy.findIndex((take) =>
      take.interactions.find((i) => i.id === interaction.id)
    );
    if (parentIndex === -1) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes3D,
        interaction.id
      );
      if (!feedbackLocation) return {};

      const newTakes = removeQuizFeedbackInteraction(
        state.takes3D,
        feedbackLocation
      );
      return {
        takes3D: newTakes,
        selectedTake: feedbackLocation.takeId,
        selectedElementId: getNextSelectedAfterFeedbackRemoval(
          newTakes,
          feedbackLocation
        ),
        selectedElementIsTake: false,
        selectedElementIsInteraction: true,
        selectedSequenceTakeId: "",
        isDirty: true,
      };
    }

    takesCopy[parentIndex].interactions.splice(
      takesCopy[parentIndex].interactions.findIndex((i) => i.id === interaction.id),
      1
    );
    return {
      takes3D: takesCopy,
      isDirty: true,
    };
  },
  updateSelectedTake(state, take) {
    const ti = selectTakeIndexById(state.takes3D, take.id);
    const copy = [...state.takes3D];
    copy[ti] = repairTake3DActorStates(take as Take3D);

    return {
      takes3D: copy,
      isDirty: true,
    };
  },
  updateSelectedTakeProperty(state: TrainingState, takeProperty: InteractionProperty) {
    const copy = [...state.takes3D];
    const ti = selectTakeIndexById(copy, state.selectedTake);
    const properties = copy[ti].properties!;
    const pi = properties.findIndex((p) => p.id === takeProperty.id);
    properties[pi] = takeProperty;

    return {
      takes3D: copy,
      isDirty: true,
    };
  },
  setTakes(state, takes) {
    const normalizedTakes = (takes as Take3D[]).map((take) => {
      const normalizedTake = normalizeTake3D(take);
      return {
        ...normalizedTake,
        transition: normalizeTransition(normalizedTake.transition),
      };
    });

    return {
      takes3D: normalizedTakes,
      selectedTake: normalizedTakes.length > 0 ? normalizedTakes[0].id : "",
      selectedElementId: normalizedTakes.length > 0 ? normalizedTakes[0].id : "",
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      selectedSequenceTakeId: "",
      isDirty: false,
    };
  },
  selectSelectedElement(state) {
    const selectedType = selectSelectedElementType(state);

    const take = state.training.takes3D.find(
      (candidate) => candidate.id === state.training.selectedTake
    );
    if (!take) return;

    if (selectedType === "take") {
      return take;
    }

    if (selectedType === "takeSequence") {
      return getTake3DSequence(take);
    }

    if (selectedType === "transition") {
      if (take.transition?.id === state.training.selectedElementId) {
        return normalizeTransition(take.transition);
      }
    }

    if (selectedType === "camera") {
      if (take.mainCamera.id === state.training.selectedElementId) {
        return take.mainCamera;
      }
    }

    if (selectedType === "interaction") {
      return (
        take.interactions.find(
          (interaction) => interaction.id === state.training.selectedElementId
        ) ??
        findQuizFeedbackInteraction(
          state.training.takes3D,
          state.training.selectedElementId
        )?.interaction
      );
    }

    if (selectedType === "actor") {
      return take.actors.find(
        (actor) => actor.id === state.training.selectedElementId
      );
    }
  },
  updateSelectedInteraction(state, interaction) {
    const copied = [...state.takes3D];
    const ti = selectTakeIndexById(copied, state.selectedTake);
    const si = selectInteractionIndexById(copied[ti], interaction.id);
    if (si === -1) {
      const feedbackLocation = findQuizFeedbackInteraction(
        state.takes3D,
        interaction.id
      );
      if (!feedbackLocation) return {};
      return {
        takes3D: replaceQuizFeedbackInteraction(
          state.takes3D,
          feedbackLocation,
          interaction
        ),
        isDirty: true,
      };
    }
    copied[ti].interactions[si] = { ...interaction };

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  updateSelectedInteractionProperty(state: TrainingState, interactionProperty: InteractionProperty) {
    const copied = [...state.takes3D];
    const ti = selectTakeIndexById(state.takes3D, state.selectedTake);

    if (
      state.selectedElementId === undefined &&
      state.takes3D[ti].mainCamera.properties &&
      state.takes3D[ti].mainCamera.properties?.find(
        (prop) => prop.id === interactionProperty.id
      )
    ) {
      copied[ti].mainCamera = UpdatePropertyOfInteraction(
        copied[ti].mainCamera,
        interactionProperty
      ) as ISetCamera;
    } else {
      const si = selectInteractionIndexById(
        state.takes3D[ti],
        state.selectedElementId
      );
      if (si === -1) {
        const feedbackLocation = findQuizFeedbackInteraction(
          state.takes3D,
          state.selectedElementId
        );
        if (!feedbackLocation) return {};
        return {
          takes3D: replaceQuizFeedbackInteraction(
            state.takes3D,
            feedbackLocation,
            UpdatePropertyOfInteraction(
              feedbackLocation.interaction,
              interactionProperty
            )
          ),
          isDirty: true,
        };
      }
      copied[ti].interactions[si] = UpdatePropertyOfInteraction(
        copied[ti].interactions[si],
        interactionProperty
      );
    }

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  deleteTake(state, takeId) {
    let index = state.takes3D.findIndex((take) => take.id === takeId) - 1;
    index = index >= 0 ? index : 0;

    const copied = [...state.takes3D];
    const newTakes = copied.filter((take) => take.id !== takeId);
    state.takes3D = newTakes;

    return {
      takes3D: newTakes,
      selectedTake: newTakes.length > 0 ? newTakes[index].id : "",
      selectedElementId: newTakes.length > 0 ? newTakes[index].id : "",
      selectedElementIsTake: true,
      selectedElementIsInteraction: false,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  reorderTakes(state, startIndex, endIndex) {
    const copied = [...state.takes3D];
    if (
      startIndex < 0 ||
      endIndex < 0 ||
      startIndex >= copied.length ||
      endIndex >= copied.length ||
      startIndex === endIndex
    ) {
      return {};
    }
    const [removed] = copied.splice(startIndex, 1);
    if (!removed) return {};
    copied.splice(endIndex, 0, removed);

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  reorderInteractions(state, startTakeId, destinationTakeId, startIndex, endIndex) {
    const copied = [...state.takes3D];
    const startTakeIndex = selectTakeIndexById(state.takes3D, startTakeId);
    const destinationTakeIndex = selectTakeIndexById(
      state.takes3D,
      destinationTakeId
    );
    if (startTakeIndex === -1 || destinationTakeIndex === -1) return {};
    const startTake = copied[startTakeIndex];
    const destTake = copied[destinationTakeIndex];
    if (startIndex < 0 || startIndex >= startTake.interactions.length) {
      return {};
    }
    if (endIndex < 0) return {};
    const [removed] = startTake.interactions.splice(startIndex, 1);
    if (!removed) return {};
    const normalizedEndIndex = Math.min(endIndex, destTake.interactions.length);
    destTake.interactions.splice(normalizedEndIndex, 0, removed);

    return {
      takes3D: copied,
      selectedTake: destTake.id,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  moveInteraction(state, payload) {
    if (
      payload.destination.kind === "quizFeedback" &&
      !areMoveSourcesFeedbackEligible(
        state.takes3D,
        [payload.interactionId],
        FEEDBACK_ELIGIBLE_TYPES_3D
      )
    ) {
      return {};
    }
    const result = moveInteraction(state.takes3D, payload);
    if (!result) return {};

    return {
      takes3D: result.takes,
      selectedTake: result.destinationTakeId,
      selectedElementId: result.interaction.id,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      isDirty: true,
    };
  },
  moveInteractions(state, payload) {
    if (
      payload.destination.kind === "quizFeedback" &&
      !areMoveSourcesFeedbackEligible(
        state.takes3D,
        payload.interactionIds,
        FEEDBACK_ELIGIBLE_TYPES_3D
      )
    ) {
      return {};
    }
    const result = moveInteractionsBatch(state.takes3D, payload);
    if (!result) return {};

    const movedIds = result.interactions.map((interaction) => interaction.id);
    const primary = movedIds.includes(state.selectedElementId)
      ? state.selectedElementId
      : movedIds[0];
    return {
      takes3D: result.takes,
      selectedTake: result.destinationTakeId,
      selectedElementId: primary,
      selectedElementIsTake: false,
      selectedElementIsInteraction: true,
      selectedSequenceTakeId: "",
      selectedInteractionIds: movedIds,
      selectionAnchorId: movedIds.includes(state.selectionAnchorId)
        ? state.selectionAnchorId
        : primary,
      isDirty: true,
    };
  },
  moveSequenceSelection(state, payload) {
    const result = moveSequenceSelection(state.takes3D, payload);
    if (!result.ok) return {};
    return {
      takes3D: result.takes as Take3D[],
      selectedTake: payload.destination.takeId,
      selectedElementId: payload.destination.sequenceId,
      selectedElementIsTake: false,
      selectedElementIsInteraction:
        payload.destination.kind === "interaction",
      selectedSequenceTakeId:
        payload.destination.kind === "takeSequence"
          ? payload.destination.takeId
          : "",
      isDirty: true,
    };
  },
  selectSelectedElementType(state: TrainingState) {
    if (state.selectedSequenceTakeId) {
      const selectedTakeSequence = state.takes3D.find(
        (take) => take.id === state.selectedSequenceTakeId
      );
      if (selectedTakeSequence) return "takeSequence";
    }

    const selectedTake = state.takes3D.find(
      (take) => take.id === state.selectedElementId
    );
    if (selectedTake) return "take";

    const selectedCamera = state.takes3D.find(
      (take) => take.mainCamera.id === state.selectedElementId
    );
    if (selectedCamera) return "camera";

    const selectedTransition = state.takes3D.find(
      (take) => take.transition?.id === state.selectedElementId
    );
    if (selectedTransition) return "transition";

    const selectedInteraction = state.takes3D.find((take) =>
      take.interactions.find(
        (interaction) => interaction.id === state.selectedElementId
      )
    );
    if (selectedInteraction) return "interaction";

    // nested quiz feedback interaction
    if (findQuizFeedbackInteraction(state.takes3D, state.selectedElementId))
      return "interaction";

    const selectedActor = state.takes3D.find((take) =>
      take.actors.find((actor) => actor.id === state.selectedElementId)
    );
    if (selectedActor) return "actor";

    return undefined;
  },
  updateSelectedActorState(state: TrainingState, actorState: ActorState) {
    const copied = [...state.takes3D];
    const take = copied.find((take) => take.id === state.selectedTake);
    if (!take) return {};

    const actorIndex = take.actors.findIndex((actor) => actor.id === actorState.id);
    if (actorIndex === -1) return {};
    take.actors[actorIndex] = normalizeActorState(actorState);
    const takeIndex = findTakeIndexById(copied, take.id);
    copied[takeIndex] = repairTake3DActorStates(take);

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  addPropToSelectedTake(state: TrainingState, instanceId: string, positionId: string) {
    const copied = [...state.takes3D];
    const takeIndex = findTakeIndexById(copied, state.selectedTake);
    if (takeIndex === -1) return {};

    const filteredProps = copied[takeIndex].props
      .filter((prop) => prop.instanceId !== instanceId)
      .filter((prop) => prop.positionId !== positionId);

    const newProp = {
      id: GetNewGuid(),
      instanceId,
      positionId,
    };

    // A placed prop is no longer held: drop an attachment that held it.
    const attachments = { ...copied[takeIndex].attachments };
    if (isHeldByAttachment(attachments, instanceId)) delete attachments[instanceId];

    copied[takeIndex] = {
      ...copied[takeIndex],
      props: [...filteredProps, newProp],
      attachments,
    };
    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  removePropFromSelectedTake(state: TrainingState, id: string) {
    const copied = [...state.takes3D];
    const takeIndex = findTakeIndexById(copied, state.selectedTake);
    if (takeIndex === -1) return {};

    copied[takeIndex] = {
      ...copied[takeIndex],
      props: copied[takeIndex].props.filter((prop) => prop.id !== id),
    };
    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  addSituationToTakeLocation(state: TrainingState, takeLocationId: string, situationId: string, fallbackCameraId: string, actorsToRemove: string[]) {
    const copied = [...state.takes3D];
    const take = findTakeById(copied, state.selectedTake);
    if (!take) return {};
    const copySituations = { ...take.situations };
    let resetOriginalCamera = false;

    for (const [key, value] of Object.entries(copySituations)) {
      if (value === situationId) {
        delete copySituations[key];
        if (take.interactionPointId === key) {
          resetOriginalCamera = true;
        }
      }
    }

    copySituations[takeLocationId] = situationId;
    take.situations = copySituations;

    if (take.interactionPointId === takeLocationId || resetOriginalCamera) {
      take.mainCamera = { ...take.mainCamera, cameraId: fallbackCameraId };
    }

    take.actors = take.actors.filter(
      (actors) => !actorsToRemove.includes(actors.id)
    );
    const takeIndex = findTakeIndexById(copied, take.id);
    copied[takeIndex] = repairTake3DActorStates(take);

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  removeSituationFromTakeLocation(state, takeLocationId, fallbackCameraId, actorsToRemove, propsToRemove) {
    const copied = [...state.takes3D];
    const take = findTakeById(copied, state.selectedTake);
    if (!take) return {};
    const copySituations = { ...take.situations };
    delete copySituations[takeLocationId];

    take.situations = copySituations;

    if (take.interactionPointId === takeLocationId) {
      take.mainCamera = { ...take.mainCamera, cameraId: fallbackCameraId };
    }

    take.actors = take.actors.filter(
      (actors) => !actorsToRemove.includes(actors.id)
    );

    take.props = take.props.filter((props) => !propsToRemove.includes(props.id));
    const takeIndex = findTakeIndexById(copied, take.id);
    copied[takeIndex] = repairTake3DActorStates(take);

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  addAttachmentToTake(state, actorId, propId, tag, playerToProp) {
    const copied = [...state.takes3D];
    const take = findTakeById(copied, state.selectedTake);
    if (!take) return {};
    const attachmentsCpy = take.attachments;

    if (!propId) return {};

    const existingAttachment = attachmentsCpy[propId];
    if (existingAttachment && existingAttachment.id !== actorId) {
      delete attachmentsCpy[propId];
    }

    const placement = take.props.find((prop) => prop.instanceId === propId);
    // A placed prop is most likely something to get on (a forklift), not to pick up.
    const toProp = playerToProp ?? Boolean(placement);
    const removedPlacement = existingAttachment?.removedPlacement;
    attachmentsCpy[propId] = {
      id: actorId,
      tag,
      playerToProp: toProp,
      ...(removedPlacement ? { removedPlacement } : {}),
    };

    if (isHeldByAttachment(attachmentsCpy, propId)) {
      // The actor holds the prop now, so it is no longer placed on its position;
      // remember where, so a switch back to Player-to-Prop can put it back.
      if (placement) {
        attachmentsCpy[propId].removedPlacement = { ...placement };
        take.props = take.props.filter((prop) => prop.instanceId !== propId);
      }
    } else if (placement || restoreRemovedPlacement(take, propId, removedPlacement)) {
      delete attachmentsCpy[propId].removedPlacement;
    }

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
  removeAttachmentFromTake(state, actorId, propId) {
    const copied = [...state.takes3D];
    const take = findTakeById(copied, state.selectedTake);
    if (!take) return {};

    const existingAttachment = take.attachments[propId];
    if (!existingAttachment || existingAttachment.id !== actorId) return {};

    delete take.attachments[propId];
    if (!take.props.some((prop) => prop.instanceId === propId))
      restoreRemovedPlacement(take, propId, existingAttachment.removedPlacement);

    return {
      takes3D: copied,
      isDirty: true,
    };
  },
};

const findTakeById = (takes: Take3D[], takeId: string) =>
  takes.find((take) => take.id === takeId);

/**
 * Puts a prop back where a Prop-to-Player attachment took it from. Skipped when
 * another prop has taken that position since: positions hold one prop.
 */
const restoreRemovedPlacement = (
  take: Take3D,
  propId: string,
  placement?: PropTakeState
) => {
  if (!placement) return false;
  if (take.props.some((prop) => prop.positionId === placement.positionId))
    return false;
  take.props = [...take.props, { ...placement, instanceId: propId }];
  return true;
};

const findTakeIndexById = (takes: Take3D[], takeId: string) =>
  takes.findIndex((take) => take.id === takeId);

const ensureEnvironmentPropStates = (
  state: TrainingState,
  props: PropTakeState[]
): PropTakeState[] => {
  const nextProps = [...props];

  state.props
    .filter(
      (prop) =>
        prop.propType === PropType.Environment && prop.environmentMetadataId
    )
    .forEach((prop) => {
      const positionId = getEnvironmentPropPositionId(
        prop.environmentMetadataId!
      );
      const exists = nextProps.some(
        (propState) =>
          propState.instanceId === prop.id || propState.positionId === positionId
      );
      if (exists) return;

      nextProps.push({
        id: GetNewGuid(),
        instanceId: prop.id,
        positionId,
      });
    });

  return nextProps;
};
